import { Box, Center, Divider, Flex, VStack, useSize } from '@hover/blueprint';
import type {
  AiImageEdits,
  AiImageMaterialEdit,
  AiImagePaintEdit,
} from 'mock/generated/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router';

import { MeasurementHeader } from 'src/modules/homeownerApp/components/Projects/Project/ScanDetailsModal';
import { StartScanCTA } from 'src/modules/homeownerApp/components/Scan/StartScanCTA';
import { hasExteriorPhoto } from 'src/modules/homeownerApp/components/Utils';
import {
  useAnchorScroll,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Feature } from 'src/shared/components/Feature';
import { FeatureFlag } from 'src/shared/constants';
import type {
  AiDisplayImage,
  Maybe,
  Project as ProjectType,
} from 'src/shared/graphql/generated';
import { useGetProjectQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { useIsMobileSize } from 'src/shared/hooks/useIsMobileSize';
import { Route } from 'src/shared/route';

import { FullscreenImage } from '../../FullscreenImage';
import type { RegionWithDisabledRegions } from '../../FullscreenImage/useHumanReadableEdits';
import {
  getSnakecaseRegionFromKey,
  mapRegionToDisplayText,
} from '../../FullscreenImage/useHumanReadableEdits';
import { ScanError } from '../../Scan/ScanError';
import { ScanPopUp } from '../../ScanPopUp';
import { StackingHeaderProvider } from '../../StackingHeader';
import { NotFoundSplash } from '../../errors';

import { ConnectPro } from './ConnectPro';
import { ExploreServices } from './ExploreServices';
import { KeyMeasurements } from './KeyMeasurements';
import { MostRecentScan } from './MostRecentScan';
import {
  HeaderSection,
  PROJECT_DETAILS_PADDING,
  ProjectCardSection,
  ProjectCardWrapper,
} from './ProjectCardSection';
import { ProjectCarousel } from './ProjectCarousel';
import type { EditedRegions } from './ProjectDetails';
import { ProjectDetails } from './ProjectDetails';
import { ProjectHeader } from './ProjectHeader';
import { ProjectSkeleton } from './ProjectSkeleton';
import type { Swatch } from './ProjectSwatches';
import { ProjectSwatch } from './ProjectSwatches';
import { ScanForMeasurements } from './ScanForMeasurements';
import {
  useAttachableScans,
  useAvailableProjectCards,
  useGetFailedProjectScans,
} from './hooks';

const MOBILE_HEADER_HEIGHT = 84;

export const inferProjectAreasFromSpaceTypes = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): string[] => {
  if (!assets) return [];
  return [
    ...new Set(
      assets
        .filter(({ spaceType }) => !!spaceType)
        .map(({ spaceType }) => spaceType as string),
    ),
  ];
};

export const inferProjectEditedRegions = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): EditedRegions => {
  if (!assets) return [];

  const res = [
    ...new Set(
      assets
        .filter(({ aiGeneratedImage }) => aiGeneratedImage?.metadata?.allEdits)
        .map(({ aiGeneratedImage, spaceType }) => ({
          spaceType: spaceType as string,
          edits: Object.entries(
            aiGeneratedImage?.metadata?.allEdits as unknown as AiImageEdits[],
          )
            .filter(([_, value]) => value?.__typename)
            .map(([key]) => key),
        })),
    ),
  ];

  return res;
};

const materialAttributes = (
  material: AiImageMaterialEdit | AiImagePaintEdit,
  asset: AiDisplayImage,
) => ({
  thumbnail:
    asset.aiGeneratedImage.imagePath ||
    asset.aiGeneratedImage.generatedImagePath,
  materialName: material.name,
  uid: `${material.name} ${asset.aiGeneratedImage.id}`,
});

type MaterialCollectionObject = {
  [key in RegionWithDisabledRegions]?: { [key: string]: Swatch };
};

type MaterialCollection = {
  [key in RegionWithDisabledRegions]?: Swatch[];
};

const formatMaterials = (
  addables: AiDisplayImage[],
  samplizeButtonText?: string,
): { paintSwatches: Swatch[]; materialCollection: MaterialCollection } => {
  const paintSwatchMap: Map<string, Swatch> = new Map();
  const materialCollectionObj: MaterialCollectionObject = {};

  addables.forEach((asset: AiDisplayImage) => {
    if (!asset.aiGeneratedImage.metadata) return;
    const { allEdits } = asset.aiGeneratedImage.metadata;
    if (!allEdits) return;
    Object.entries(allEdits).forEach(([key, category]) => {
      // NOTE: This is pretty messy, but this data structure is also intended to change
      // with the material configurability project, so we should have a cleaner way to reference
      // materials at that point in time
      const region = getSnakecaseRegionFromKey(key as keyof AiImageEdits);
      if (
        !region ||
        !category ||
        typeof category === 'number' ||
        category === 'AiImageEdits' ||
        // TODO: handle `instances` edit model
        Array.isArray(category) ||
        !(
          category.__typename === 'AiImageRegionEdits' ||
          category.__typename === 'AiImageWallEdits'
        )
      ) {
        return;
      }

      const { paint, material } = category;

      if (paint && region) {
        const paintSwatchKey = `${paint.hex}${region}`;
        const existingPaintSwatch = paintSwatchMap.get(paintSwatchKey);

        if (existingPaintSwatch) {
          existingPaintSwatch.assets.push(asset);
        } else {
          const paintSwatch = {
            color: paint.hex,
            subtitle: paint.manufacturer,
            linkOutCta: paint.samplizeProductKey ? samplizeButtonText : '',
            linkOutUrl: paint.samplizeProductKey
              ? `https://co.samplize.com/products/${paint.samplizeProductKey}?utm_source=hover&utm_medium=referral&utm_campaign=hover_p1`
              : '',
            region,
            ...materialAttributes(paint, asset),
            assets: [asset],
          };
          paintSwatchMap.set(paintSwatchKey, paintSwatch);
        }
      }

      if (material && region) {
        const materialSwatchKey = `${material.hex || ''}:${material.name}`;
        if (!materialCollectionObj[region]) {
          materialCollectionObj[region] = {};
        }

        const regionMaterialCollection = materialCollectionObj[region];
        const existingMaterialSwatch =
          regionMaterialCollection?.[materialSwatchKey];

        if (existingMaterialSwatch) {
          existingMaterialSwatch.assets.push(asset);
        } else {
          regionMaterialCollection![materialSwatchKey] = {
            color: material.hex || undefined,
            image: material.thumbnail || undefined,
            ...materialAttributes(material, asset),
            assets: [asset],
          };
        }
      }
    });
  });

  const materialCollection: MaterialCollection = {};
  Object.entries(materialCollectionObj).forEach(([region, value]) => {
    materialCollection[region as RegionWithDisabledRegions] =
      Object.values(value);
  });

  return {
    paintSwatches: Array.from(paintSwatchMap.values()),
    materialCollection,
  };
};

const getProjectImages = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): AiDisplayImage[] => {
  const images: AiDisplayImage[] = [];
  assets?.forEach(asset => {
    images.push(asset);
  }, []);

  return images;
};

const Project = () => {
  const location = useLocation();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { projectId } = useParams<{ projectId: string }>();
  const projectNameVisibilityHandler = useRef<HTMLDivElement>(null);
  const comingSoonRef = useRef<HTMLDivElement>(null);
  const materialsRef = useRef<HTMLDivElement>(null);
  const measurementsRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const isMobileSize = useIsMobileSize();
  const [seenScanPopup, setSeenScanPopup] = useState(false);
  const match = useRouteMatch({
    path: Route.HOMEOWNER_APP_PROJECT.path,
    strict: true,
  });
  const isPropertyDetails = !!match?.isExact;
  const navDimensions = useSize(navRef);

  const sectionToRef: Record<string, React.RefObject<HTMLDivElement>> = useMemo(
    () => ({
      comingSoon: comingSoonRef,
      measurements: measurementsRef,
      materials: materialsRef,
      services: servicesRef,
      help: helpRef,
    }),
    [comingSoonRef, materialsRef, measurementsRef, servicesRef, helpRef],
  );

  const navBarHeight = navDimensions?.height ?? 0;
  const scrollTop = -(isMobileSize
    ? navBarHeight + MOBILE_HEADER_HEIGHT
    : navBarHeight);
  const scrollToRef = useAnchorScroll(scrollTop);
  const { setCurrentMenu, user } = useHomeownerAppContext();

  const { data, loading } = useGetProjectQuery({
    variables: {
      projectId: Number(projectId),
    },
  });

  useEffectOnMount(() => {
    if (location.hash) {
      scrollToRef(sectionToRef[location.hash.slice(1)]);
    } else {
      window.scrollTo(0, 0);
    }
  });

  const project = data?.project as ProjectType | undefined;
  const addables: AiDisplayImage[] = useMemo(
    () => project?.addables || [],
    [project],
  );
  const images = getProjectImages(addables) || [];
  const isExteriorProject = images?.[0]?.spaceType === 'exterior';

  const projectFailedScan = useGetFailedProjectScans(project);
  const showScanError = !!projectFailedScan;

  const { paintSwatches, materialCollection } = useMemo(
    () => formatMaterials(addables),
    [addables],
  );

  const attachedScan = project?.jobs?.[0];
  const attachableScans = useAttachableScans();
  const mostRecentAttachableScan = attachableScans[0];
  const hasExterior = hasExteriorPhoto(data?.project?.addables);
  const isHomeowner = !!user?.isHomeowner;
  const showAttachScan =
    hasExterior && mostRecentAttachableScan && !attachedScan;

  useEffect(() => {
    if (!seenScanPopup && attachedScan) {
      setSeenScanPopup(true);
    }
  }, [attachedScan, seenScanPopup]);

  const {
    showComingSoon,
    showConnectPro,
    showExploreServices,
    scanForMeasurementsConfig,
    connectWithAProConfig,
    exploreServicesConfigs,
  } = useAvailableProjectCards(project);

  // for show the scans pop up project must have exterior
  // area (this will change in the future)
  // user should have at least an exterior type scan
  // project shouldn't have any scans yet
  const showScanPopUp = useMemo(
    () =>
      isPropertyDetails &&
      isHomeowner &&
      !seenScanPopup &&
      hasExterior &&
      !attachedScan &&
      !!attachableScans.length,
    [
      attachableScans.length,
      attachedScan,
      hasExterior,
      isHomeowner,
      isPropertyDetails,
      seenScanPopup,
    ],
  );
  const materials = useMemo(
    () => Object.entries(materialCollection),
    [materialCollection],
  );

  if (loading) return <ProjectSkeleton />;
  if (!project) return <NotFoundSplash />;

  // Only show coming soon module if it's enabled and measurement is not enabled
  // or if the project is interior only
  const ANCHOR_SECTION_TO_IS_VISIBLE: Record<string, boolean> = {
    comingSoon: showComingSoon && !hasExterior,
    materials:
      !!paintSwatches.length ||
      (!!Object.keys(materialCollection).length && !!materials.length),
    measurements: !!attachableScans.length,
    services: showExploreServices,
    help: showConnectPro,
  };

  const handleMoreActionsClick = () => {
    setCurrentMenu(HomeownerAppMenuNames.PROJECT_MORE_ACTIONS);
  };

  const handleNavButtonClick = (sectionName: string) => {
    scrollToRef(sectionToRef[sectionName]);
  };
  const paintTitle = t('projects.swatch.paint.title');

  return (
    <StackingHeaderProvider
      height={{
        desktop: { expanded: 0, collapsed: 0 },
        mobile: { expanded: 80, collapsed: 80 },
      }}
    >
      {showScanPopUp && (
        <ScanPopUp
          onDisplayed={() => setSeenScanPopup(true)}
          projectId={projectId}
          scans={attachableScans}
        />
      )}
      <Center height="100%">
        <Box flexDirection={{ base: 'column', tablet: 'row' }} width="100%">
          <FullscreenImage
            images={images}
            path={generatePath(
              Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
              { projectId, aiDisplayImageId: ':aiDisplayImageId' },
            )}
            returnPath={generatePath(Route.HOMEOWNER_APP_PROJECT.path, {
              projectId,
            })}
          >
            <ProjectCarousel
              addables={project?.addables}
              projectNameVisibilityHandler={projectNameVisibilityHandler}
              width={{ base: '100%', tablet: '55%' }}
            />
          </FullscreenImage>
          <Box
            boxSizing="border-box"
            flexDirection="column"
            width={{ base: '100%', tablet: '45%' }}
          >
            <ProjectHeader
              id={project.id}
              name={project.name}
              onMoreActionsClick={handleMoreActionsClick}
              projectNameVisibilityHandler={projectNameVisibilityHandler}
            />
            {/*  Project Details */}
            <ProjectDetails
              editedRegions={inferProjectEditedRegions(project?.addables)}
              name={project.name}
              navItems={Object.keys(ANCHOR_SECTION_TO_IS_VISIBLE).filter(
                key => !!ANCHOR_SECTION_TO_IS_VISIBLE[key],
              )}
              navRef={navRef}
              onNavButtonClick={handleNavButtonClick}
              overflow="unset"
              projectId={project.id}
              pt={{ base: 700, tablet: 0 }}
              px={PROJECT_DETAILS_PADDING}
            />
            {/* /coming soon */}
            <Flex flexDirection="column" gap={950} mb={950} width="100%">
              {ANCHOR_SECTION_TO_IS_VISIBLE.comingSoon && (
                <Flex px={PROJECT_DETAILS_PADDING}>
                  {/* coming soon */}
                  <div id="comingSoon" ref={comingSoonRef}>
                    <ScanForMeasurements
                      config={scanForMeasurementsConfig}
                      isExteriorProject={isExteriorProject}
                      projectId={projectId}
                    />
                  </div>
                </Flex>
              )}
              {isHomeowner && !showAttachScan && !attachableScans.length && (
                <Feature name={FeatureFlag.Designer.START_SCAN}>
                  <StartScanCTA />
                </Feature>
              )}
              {/* Measurements */}
              {ANCHOR_SECTION_TO_IS_VISIBLE.measurements && (
                <div id="measurements" ref={measurementsRef}>
                  {/* Scan Error */}
                  <Feature name={FeatureFlag.Designer.START_SCAN}>
                    {isHomeowner && showScanError && (
                      <ScanError
                        projectId={projectId}
                        scan={projectFailedScan}
                      />
                    )}
                  </Feature>
                  {isHomeowner && showAttachScan && (
                    <MostRecentScan
                      projectId={projectId}
                      scan={mostRecentAttachableScan}
                    />
                  )}
                  {isHomeowner && attachedScan && !showScanError && (
                    <>
                      <Feature name={FeatureFlag.Designer.START_SCAN}>
                        <MeasurementHeader
                          projectId={Number(projectId)}
                          scan={attachedScan}
                        />
                      </Feature>
                      <KeyMeasurements
                        jobId={attachedScan.id}
                        projectId={Number(projectId)}
                      />
                      <Divider
                        display={{ tablet: 'none' }}
                        mb={300}
                        mt={500}
                        mx={PROJECT_DETAILS_PADDING}
                        width="auto"
                      />
                    </>
                  )}
                </div>
              )}
              {/* materials */}
              {ANCHOR_SECTION_TO_IS_VISIBLE.materials && (
                <Box flexDir="column" ref={materialsRef} w="1">
                  {!!paintSwatches.length && (
                    <VStack alignItems="flex-start">
                      <HeaderSection
                        subtitle={t('projects.swatch.paint.subtitle')}
                        title={paintTitle}
                      />
                      <ProjectCardSection>
                        {paintSwatches.map(swatch => (
                          <ProjectCardWrapper key={swatch.uid}>
                            <ProjectSwatch
                              {...swatch}
                              projectId={projectId}
                              title={paintTitle}
                            />
                          </ProjectCardWrapper>
                        ))}
                      </ProjectCardSection>
                    </VStack>
                  )}
                  <Flex flexDir="column" gap="950" id="materials">
                    {materials.map(([region, materialSwatches]) => (
                      <VStack alignItems="flex-start" key={`material${region}`}>
                        <HeaderSection
                          subtitle={t('projects.swatch.material.subtitle')}
                          title={mapRegionToDisplayText(
                            t,
                            region as RegionWithDisabledRegions,
                          )}
                        />
                        <ProjectCardSection>
                          {materialSwatches.map(swatch => (
                            <ProjectCardWrapper key={swatch.uid}>
                              <ProjectSwatch
                                {...swatch}
                                projectId={projectId}
                                title={mapRegionToDisplayText(
                                  t,
                                  region as RegionWithDisabledRegions,
                                )}
                              />
                            </ProjectCardWrapper>
                          ))}
                        </ProjectCardSection>
                      </VStack>
                    ))}
                  </Flex>
                </Box>
              )}
              {/* services */}
              {ANCHOR_SECTION_TO_IS_VISIBLE.services && (
                <div id="services" ref={servicesRef}>
                  <ExploreServices
                    projectId={projectId}
                    services={exploreServicesConfigs
                      .filter(({ name }) => project.links?.includes(name))
                      .map(({ config: serviceConfig }) => serviceConfig)}
                  />
                </div>
              )}
            </Flex>
            {/* /services */}
            {/* help */}
            {ANCHOR_SECTION_TO_IS_VISIBLE.help && (
              <div id="help" ref={helpRef}>
                <ConnectPro
                  config={connectWithAProConfig}
                  projectId={projectId}
                />
              </div>
            )}
            {/* /help */}
          </Box>
        </Box>
      </Center>
    </StackingHeaderProvider>
  );
};

export { Project };
